import React, {useState} from "react";
import {
    Avatar,
    Button,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Typography,
    Divider,
    Box,
    Chip,
} from "@mui/material";
import {
    AccountCircle,
    ContactPage,
    Email,
    ExitToApp,
    Folder,
    FormatQuote as FormatQuoteIcon,
    Newspaper as NewspaperIcon,
    People,
    SupervisorAccount as SupervisorAccountIcon,
    School as SchoolIcon,
    CalendarMonth as CalendarMonthIcon,
    EventAvailable as EventAvailableIcon,
} from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {useNavigate} from "react-router";
import {useCheckIfAdmin} from "../firebase/useCheckIfAdmin";
import {useIsInstructorOrAdmin} from "../firebase/useIsInstructorOrAdmin";
import {useFirebaseState} from "../firebase/firebaseContext";

export const UserAvatar = ({user}) => {
    const navigate = useNavigate();
    const isAdmin = useCheckIfAdmin();
    const isInstructor = useIsInstructorOrAdmin();
    const {auth} = useFirebaseState();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNavigate = (path) => {
        handleClose();
        navigate(path);
    };

    const handleSignOut = () => {
        handleClose();
        if (auth?.signOut) {
            auth.signOut();
        }
        navigate("/");
    };

    const displayName = user?.displayName || "Bruker";
    const email = user?.email || "";

    return (
        <div>
            <Button
                onClick={handleOpen}
                aria-label={`Åpne brukermeny for ${displayName}`}
                aria-controls={open ? "user-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                sx={{
                    p: {xs: 0.5, sm: "4px 10px 4px 6px"},
                    borderRadius: 4,
                    border: "1px solid",
                    borderColor: open ? "primary.main" : "rgba(40, 82, 56, 0.2)",
                    bgcolor: open ? "rgba(40, 82, 56, 0.08)" : "background.paper",
                    textTransform: "none",
                    minWidth: 44,
                    minHeight: 44,
                    transition: "all 0.15s ease-in-out",
                    "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "rgba(40, 82, 56, 0.06)",
                    },
                    "&:focus-visible": {
                        outline: "2px solid #285238",
                        outlineOffset: 2,
                    },
                }}
            >
                <Avatar
                    alt={displayName}
                    src={user?.photoURL}
                    sx={{width: 32, height: 32, mr: {xs: 0, sm: 1}}}
                />
                <Typography
                    variant="body2"
                    sx={{
                        display: {xs: "none", sm: "block"},
                        fontWeight: 700,
                        color: "text.primary",
                        maxWidth: {sm: 100, md: 150},
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {displayName}
                </Typography>
                <Chip
                    label={isAdmin ? "Admin" : isInstructor ? "Kursholder" : "Min side"}
                    size="small"
                    sx={{
                        display: {xs: "none", md: "inline-flex"},
                        ml: 1,
                        height: 20,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        bgcolor: "rgba(40, 82, 56, 0.1)",
                        color: "primary.main",
                        borderRadius: 1.5,
                    }}
                />
                <KeyboardArrowDownIcon
                    sx={{
                        fontSize: 18,
                        color: "text.secondary",
                        ml: 0.5,
                        display: {xs: "none", sm: "block"},
                        transition: "transform 0.2s",
                        transform: open ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                />
            </Button>

            <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                disableScrollLock={true}
                transformOrigin={{horizontal: "right", vertical: "top"}}
                anchorOrigin={{horizontal: "right", vertical: "bottom"}}
                slotProps={{
                    paper: {
                        elevation: 3,
                        sx: {
                            mt: 1.5,
                            minWidth: 240,
                            borderRadius: 3,
                            border: "1px solid rgba(40, 82, 56, 0.12)",
                            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
                            p: 0.5,
                            "& .MuiMenuItem-root": {
                                borderRadius: 1.5,
                                my: 0.25,
                                minHeight: 40,
                                "&:hover": {
                                    bgcolor: "rgba(40, 82, 56, 0.08)",
                                },
                            },
                        },
                    },
                }}
            >
                {/* User Info Header */}
                <Box sx={{px: 2, py: 1.25}}>
                    <Typography variant="subtitle2" sx={{fontWeight: 700, color: "text.primary"}}>
                        {displayName}
                    </Typography>
                    {email && (
                        <Typography variant="caption" color="text.secondary" sx={{display: "block", overflow: "hidden", textOverflow: "ellipsis"}}>
                            {email}
                        </Typography>
                    )}
                    <Chip
                        label={isAdmin ? "Administrator" : isInstructor ? "Sertifisert Kursholder" : "Bruker"}
                        size="small"
                        sx={{
                            mt: 0.75,
                            height: 20,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            bgcolor: "primary.main",
                            color: "common.white",
                        }}
                    />
                </Box>
                <Divider sx={{my: 0.5}} />

                <MenuItem onClick={() => handleNavigate("/me")}>
                    <ListItemIcon>
                        <AccountCircle fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Min profil</ListItemText>
                </MenuItem>

                <MenuItem onClick={() => handleNavigate("/calendar")}>
                    <ListItemIcon>
                        <CalendarMonthIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Kurskalender</ListItemText>
                </MenuItem>

                {isInstructor && [
                    <MenuItem key="instructor-courses" onClick={() => handleNavigate("/me")}>
                        <ListItemIcon>
                            <EventAvailableIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Mine kurs & henvendelser</ListItemText>
                    </MenuItem>,
                    <MenuItem key="instructor-profile" onClick={() => handleNavigate("/instructor-profile")}>
                        <ListItemIcon>
                            <ContactPage fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Instruktørprofil</ListItemText>
                    </MenuItem>,
                    <MenuItem key="instructor-files" onClick={() => handleNavigate("/files")}>
                        <ListItemIcon>
                            <Folder fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Kursfiler og materiell</ListItemText>
                    </MenuItem>,
                ]}

                {isAdmin && [
                    <MenuItem key="admin-training" onClick={() => handleNavigate("/admin/training")}>
                        <ListItemIcon>
                            <SchoolIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Kurslederopplæring</ListItemText>
                    </MenuItem>,
                    <MenuItem key="admin-users" onClick={() => handleNavigate("/admin/users")}>
                        <ListItemIcon>
                            <People fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Brukeradministrasjon</ListItemText>
                    </MenuItem>,
                    <MenuItem key="admin-instructors" onClick={() => handleNavigate("/administrate-instructors")}>
                        <ListItemIcon>
                            <SupervisorAccountIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Kursholderadministrasjon</ListItemText>
                    </MenuItem>,
                    <MenuItem key="admin-news" onClick={() => handleNavigate("/admin/news")}>
                        <ListItemIcon>
                            <NewspaperIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Nyhetsadministrasjon</ListItemText>
                    </MenuItem>,
                    <MenuItem key="admin-testimonials" onClick={() => handleNavigate("/admin/testimonials")}>
                        <ListItemIcon>
                            <FormatQuoteIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Tilbakemeldinger</ListItemText>
                    </MenuItem>,
                    <MenuItem key="admin-email" onClick={() => handleNavigate("/send-email-to-instructors")}>
                        <ListItemIcon>
                            <Email fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Send e-post</ListItemText>
                    </MenuItem>,
                ]}

                <Divider sx={{my: 0.5}} />
                <MenuItem onClick={handleSignOut} sx={{color: "error.main"}}>
                    <ListItemIcon sx={{color: "error.main"}}>
                        <ExitToApp fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Logg ut</ListItemText>
                </MenuItem>
            </Menu>
        </div>
    );
};